import { ref, Ref } from "vue";

interface TabPurchaseSyncOptions {
  /**
   * デバッグログを出力するか
   * デフォルト: false
   */
  enableDebugLog?: boolean;
}

interface TabPurchaseSyncReturn {
  /**
   * タブが現在表示されているかどうか
   */
  isTabVisible: Readonly<Ref<boolean>>;
  /**
   * 手動でデータ同期をトリガーする関数
   */
  triggerSync: () => Promise<void>;
  /**
   * イベントリスナーをクリーンアップする関数
   * コンポーネントの beforeDestroy で呼び出す
   */
  cleanup: () => void;
}

/**
 * タブ復帰時のデータ同期を管理するComposable
 * タブが非表示から表示に変わったときに自動的に同期処理を実行する
 *
 * @param syncCallback - タブ復帰時に実行する同期処理
 * @param options - オプション設定
 * @returns タブ状態管理とメソッド
 *
 * @example
 * ```typescript
 * // FishCounter での使用例
 * const { cleanup } = useTabPurchaseSync(
 *   async () => {
 *     await store.dispatch("fishModule/fetchFishData");
 *   },
 *   { enableDebugLog: false }
 * );
 *
 * // beforeDestroy で呼び出す
 * beforeDestroy() {
 *   cleanup();
 * }
 * ```
 */
export const useTabPurchaseSync = (
  syncCallback: () => Promise<void>,
  options: TabPurchaseSyncOptions = {}
): TabPurchaseSyncReturn => {
  const { enableDebugLog = false } = options;

  // リアクティブな状態
  const isTabVisible = ref(!document.hidden);

  // デバッグログ用のヘルパー
  const debugLog = (message: string, ...args: any[]) => {
    if (enableDebugLog) {
      console.log(`[useTabPurchaseSync] ${message}`, ...args);
    }
  };

  /**
   * タブの表示状態変更時の処理
   */
  const handleVisibilityChange = async (): Promise<void> => {
    const isNowVisible = !document.hidden;
    const wasHidden = !isTabVisible.value;

    debugLog("Visibility changed", { isNowVisible, wasHidden });

    // タブ表示状態を更新
    isTabVisible.value = isNowVisible;

    // 非表示から表示に変わった時（タブに復帰した時）の処理
    if (isNowVisible && wasHidden) {
      debugLog("Tab returned, executing sync...");
      try {
        await syncCallback();
        debugLog("Sync completed successfully");
      } catch (error) {
        console.error("[useTabPurchaseSync] Sync failed:", error);
      }
    }
  };

  /**
   * 手動で同期をトリガー
   */
  const triggerSync = async (): Promise<void> => {
    debugLog("Manual sync triggered");
    try {
      await syncCallback();
      debugLog("Manual sync completed successfully");
    } catch (error) {
      console.error("[useTabPurchaseSync] Manual sync failed:", error);
      throw error;
    }
  };

  // イベントリスナーの追加
  const addEventListeners = (): void => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    debugLog("Event listeners added");
  };

  // イベントリスナーの削除
  const removeEventListeners = (): void => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    debugLog("Event listeners removed");
  };

  // 初期化
  addEventListeners();

  return {
    isTabVisible: isTabVisible as Readonly<Ref<boolean>>,
    triggerSync,
    cleanup: removeEventListeners,
  };
};

export default useTabPurchaseSync;
